import { useState, useMemo, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, Upload, Plus, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
  parseDecimal,
  parseIntPositive,
  padCode,
  parseUf,
  parseExigeGrupo,
  validateRows,
  formatValidationErrors,
  type ValidationError,
} from "@/lib/fiscalImportUtils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useLocation } from "wouter";

const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

export default function CBS_IBS() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [isUpdateBulkDialogOpen, setIsUpdateBulkDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [municipioSearchTerm, setMunicipioSearchTerm] = useState("");
  const [selectedMunicipioNome, setSelectedMunicipioNome] = useState("");
  const [isNatureModalOpen, setIsNatureModalOpen] = useState(false);
  const [natureSearchTerm, setNatureSearchTerm] = useState("");
  const [isEmitenteModalOpen, setIsEmitenteModalOpen] = useState(false);
  const [isBulkNatureModalOpen, setIsBulkNatureModalOpen] = useState(false);
  const [bulkNatureSearchTerm, setBulkNatureSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    ufOrigem: "",
    municipioIbgeId: 0,
    aliquotaCbs: "",
    aliquotaIbsUf: "",
    aliquotaIbsMun: "",
    cClasstrib: "",
    cst: "",
    naturezaOperacaoId: 0,
    naturezaOperacaoNome: "",
    exigeGrupo: true,
    gatilho: "",
    emitenteId: undefined as number | undefined,
    emitenteNome: "",
  });

  const [bulkEditData, setBulkEditData] = useState({
    aliquotaCbs: "",
    aliquotaIbsUf: "",
    aliquotaIbsMun: "",
    cClasstrib: "",
    cst: "",
    naturezaOperacaoId: 0,
    exigeGrupo: "" as "" | "sim" | "nao",
    gatilho: "",
  });

  const { data: cbsIbsList = [], refetch } = trpc.cbsIbs.list.useQuery();
  const { data: naturezas = [] } = trpc.operationNatures.list.useQuery();
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const { data: municipios = [] } = trpc.municipiosIbge.search.useQuery(
    { searchTerm: municipioSearchTerm },
    { enabled: municipioSearchTerm.length >= 2 }
  );

  const createMutation = trpc.cbsIbs.create.useMutation({
    onSuccess: () => {
      toast.success("CBS/IBS cadastrado com sucesso!");
      refetch();
      resetForm();
    },
    onError: (error) => {
      toast.error(`Erro ao cadastrar: ${error.message}`);
    },
  });

  const updateMutation = trpc.cbsIbs.update.useMutation({
    onSuccess: () => {
      toast.success("CBS/IBS atualizado com sucesso!");
      refetch();
      resetForm();
    },
    onError: (error) => {
      toast.error(`Erro ao atualizar: ${error.message}`);
    },
  });

  const updateBulkMutation = trpc.cbsIbs.updateBulk.useMutation({
    onSuccess: () => {
      toast.success("CBS/IBS atualizados em massa!");
      refetch();
      setIsBulkDialogOpen(false);
      setSelectedIds([]);
      setBulkEditData((prev) => ({ ...prev, exigeGrupo: "" }));
    },
    onError: (error) => {
      toast.error(`Erro ao atualizar em massa: ${error.message}`);
    },
  });

  const deleteMutation = trpc.cbsIbs.delete.useMutation({
    onSuccess: () => {
      toast.success("CBS/IBS excluídos com sucesso!");
      refetch();
      setSelectedIds([]);
    },
    onError: (error) => {
      toast.error(`Erro ao excluir: ${error.message}`);
    },
  });

  const createBulkMutation = trpc.cbsIbs.createBulk.useMutation({
    onSuccess: () => {
      toast.success("CBS/IBS importados com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(`Erro ao importar: ${error.message}`);
    },
  });

  const filteredList = useMemo(() => {
    if (!searchTerm) return cbsIbsList;
    const lower = searchTerm.toLowerCase();
    return cbsIbsList.filter(
      (item: any) =>
        item.ufOrigem?.toLowerCase().includes(lower) ||
        item.municipioNome?.toLowerCase().includes(lower) ||
        item.aliquotaCbs?.toString().includes(lower) ||
        item.aliquotaIbsUf?.toString().includes(lower) ||
        item.aliquotaIbsMun?.toString().includes(lower) ||
        item.cClasstrib?.toLowerCase().includes(lower) ||
        item.cst?.toLowerCase().includes(lower) ||
        item.naturezaNome?.toLowerCase().includes(lower)
    );
  }, [cbsIbsList, searchTerm]);

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }


  function resetForm() {
    setFormData({
      ufOrigem: "",
      municipioIbgeId: 0,
      aliquotaCbs: "",
      aliquotaIbsUf: "",
      aliquotaIbsMun: "",
      cClasstrib: "",
      cst: "",
      naturezaOperacaoId: 0,
      naturezaOperacaoNome: "",
      exigeGrupo: true,
      gatilho: "",
      emitenteId: undefined,
      emitenteNome: "",
    });
    setEditingId(null);
    setIsDialogOpen(false);
    setMunicipioSearchTerm("");
    setSelectedMunicipioNome("");
  }

  function handleOpenDialog(id?: number) {
    if (id) {
      const item = cbsIbsList.find((c: any) => c.id === id);
      if (item) {
        setFormData({
          ufOrigem: item.ufOrigem || "",
          municipioIbgeId: item.municipioIbgeId || 0,
          aliquotaCbs: item.aliquotaCbs || "",
          aliquotaIbsUf: item.aliquotaIbsUf || "",
          aliquotaIbsMun: item.aliquotaIbsMun || "",
          cClasstrib: item.cClasstrib || "",
          cst: item.cst || "",
          naturezaOperacaoId: item.naturezaOperacaoId || 0,
          naturezaOperacaoNome: item.naturezaOperacaoNome || "",
          exigeGrupo: item.exigeGrupo !== undefined ? !!item.exigeGrupo : true,
          gatilho: item.gatilho || "",
          emitenteId: item.emitenteId || undefined,
          emitenteNome: item.emitenteNome || "",
        });
        setSelectedMunicipioNome(item.municipioNome || "");
        setEditingId(id);
      }
    }
    setIsDialogOpen(true);
  }

  function handleSubmit() {
    if (!formData.ufOrigem || !formData.municipioIbgeId || !formData.aliquotaCbs || !formData.aliquotaIbsUf) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    const payload = { ...formData, gatilho: formData.gatilho || undefined };
    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function handleBulkEdit() {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um registro");
      return;
    }
    const payload: any = {};
    if (bulkEditData.aliquotaCbs !== "") payload.aliquotaCbs = bulkEditData.aliquotaCbs;
    if (bulkEditData.aliquotaIbsUf !== "") payload.aliquotaIbsUf = bulkEditData.aliquotaIbsUf;
    if (bulkEditData.aliquotaIbsMun !== "") payload.aliquotaIbsMun = bulkEditData.aliquotaIbsMun;
    if (bulkEditData.cClasstrib !== "") payload.cClasstrib = bulkEditData.cClasstrib;
    if (bulkEditData.cst !== "") payload.cst = bulkEditData.cst;
    if (bulkEditData.naturezaOperacaoId !== 0) payload.naturezaOperacaoId = bulkEditData.naturezaOperacaoId;
    if (bulkEditData.gatilho !== "") payload.gatilho = bulkEditData.gatilho;
    if (bulkEditData.exigeGrupo !== "") payload.exigeGrupo = bulkEditData.exigeGrupo === "sim";
    if (Object.keys(payload).length === 0) {
      toast.error("Preencha pelo menos um campo para atualizar");
      return;
    }
    updateBulkMutation.mutate({ ids: selectedIds, ...payload });
  }

  function handleDelete() {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um registro");
      return;
    }
    if (confirm(`Excluir ${selectedIds.length} registro(s)?`)) {
      deleteMutation.mutate({ ids: selectedIds });
    }
  }

  function handleExport() {
    const data = filteredList.map((item: any) => ({
      "UF Origem": item.ufOrigem,
      "Município ID": item.municipioIbgeId,
      "Município": item.municipioNome || "",
      "Alíquota CBS (%)": item.aliquotaCbs,
      "Alíquota IBS UF (%)": item.aliquotaIbsUf,
      "Alíquota IBS Mun (%)": item.aliquotaIbsMun,
      "C_CLASSTRIB": item.cClasstrib || "",
      "CST": item.cst || "",
      "Natureza ID": item.naturezaOperacaoId || "",
      "Natureza de Operação": item.naturezaNome || "",
      "Exige Grupo": item.exigeGrupo ? "Sim" : "Não",
      "Gatilho": item.gatilho || "",
      "Emitente ID": item.emitenteId || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CBS_IBS");
    XLSX.writeFile(wb, "cbs_ibs.xlsx");
    toast.success("Exportado com sucesso!");
  }

  function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws);

      const errors = validateRows(data as any[], (row, i) => {
        const errs: ValidationError[] = [];
        const linha = i + 2;
        if (!parseUf(row["UF Origem"])) errs.push({ linha, campo: "UF Origem", motivo: "obrigatório, deve ter 2 letras" });
        if (parseIntPositive(row["Município ID"]) === null) errs.push({ linha, campo: "Município ID", motivo: "obrigatório, deve ser inteiro positivo" });
        if (parseDecimal(row["Alíquota CBS (%)"]) === null) errs.push({ linha, campo: "Alíquota CBS (%)", motivo: "obrigatório, deve ser numérico" });
        if (parseDecimal(row["Alíquota IBS UF (%)"]) === null) errs.push({ linha, campo: "Alíquota IBS UF (%)", motivo: "obrigatório, deve ser numérico" });
        if (parseDecimal(row["Alíquota IBS Mun (%)"]) === null) errs.push({ linha, campo: "Alíquota IBS Mun (%)", motivo: "obrigatório, deve ser numérico" });
        const cstRaw = row["CST"];
        if (cstRaw !== undefined && cstRaw !== null && cstRaw !== "" && padCode(cstRaw, 3) === null) errs.push({ linha, campo: "CST", motivo: "deve conter até 3 dígitos numéricos" });
        if (parseExigeGrupo(row["Exige Grupo"]) === null) errs.push({ linha, campo: "Exige Grupo", motivo: "obrigatório, use Sim ou Não" });
        return errs;
      });

      if (errors.length > 0) {
        toast.error(formatValidationErrors(errors), { duration: 8000 });
        return;
      }

      const items = (data as any[]).map((row: any) => ({
        ufOrigem: parseUf(row["UF Origem"]) as string,
        municipioIbgeId: parseIntPositive(row["Município ID"]) as number,
        aliquotaCbs: String(parseDecimal(row["Alíquota CBS (%)"])),
        aliquotaIbsUf: String(parseDecimal(row["Alíquota IBS UF (%)"])),
        aliquotaIbsMun: String(parseDecimal(row["Alíquota IBS Mun (%)"])),
        cClasstrib: row["C_CLASSTRIB"] != null ? String(row["C_CLASSTRIB"]) : "",
        cst: padCode(row["CST"], 3) ?? "",
        naturezaOperacaoId: parseIntPositive(row["Natureza ID"]) ?? undefined,
        exigeGrupo: parseExigeGrupo(row["Exige Grupo"]) as boolean,
        gatilho: row["Gatilho"] ?? row["gatilho"] ?? undefined,
        emitenteId: parseIntPositive(row["Emitente ID"]) ?? undefined,
      }));

      createBulkMutation.mutate({ items });
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  }

  function toggleSelectAll() {
    if (selectedIds.length === filteredList.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredList.map((item: any) => item.id));
    }
  }

  function toggleSelect(id: number) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">CBS / IBS</h1>
        <p className="text-muted-foreground">Gerencie as alíquotas de CBS/IBS por município</p>
      </div>

      <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex-1 min-w-[300px]">
          <Label>Buscar</Label>
          <Input
            placeholder="Buscar por UF, município, alíquota, C_CLASSTRIB ou natureza..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
          <Button variant="outline" asChild>
            <label>
              <Upload className="w-4 h-4 mr-2" />
              Importar
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
            </label>
          </Button>
          <Button variant="outline" onClick={() => setIsBulkDialogOpen(true)} disabled={selectedIds.length === 0}>
            <Edit className="w-4 h-4 mr-2" />
            Alterar em Massa
          </Button>
          <Button variant="outline" onClick={handleDelete} disabled={selectedIds.length === 0}>
            <Trash2 className="w-4 h-4 mr-2" />
            Excluir
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Novo CBS/IBS
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="p-3 text-left">
                <Checkbox
                  checked={selectedIds.length === filteredList.length && filteredList.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
              </th>
              <th className="p-3 text-left">UF Origem</th>
              <th className="p-3 text-left">Município Origem</th>
              <th className="p-3 text-left">Alíquota CBS (%)</th>
              <th className="p-3 text-left">IBS UF (%)</th>
              <th className="p-3 text-left">IBS Mun (%)</th>
              <th className="p-3 text-left">C_CLASSTRIB</th>
              <th className="p-3 text-left">CST</th>
              <th className="p-3 text-left">Exige Grupo XML</th>
              <th className="p-3 text-left">Natureza de Operação</th>
              <th className="p-3 text-left">Emitente</th>
              <th className="p-3 text-left">Gatilho</th>
              <th className="p-3 text-left">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.map((item: any) => (
              <tr key={item.id} className="border-t hover:bg-muted/50">
                <td className="p-3">
                  <Checkbox
                    checked={selectedIds.includes(item.id)}
                    onCheckedChange={() => toggleSelect(item.id)}
                  />
                </td>
                <td className="p-3">{item.ufOrigem}</td>
                <td className="p-3">{item.municipioNome}</td>
                <td className="p-3">{item.aliquotaCbs}</td>
                <td className="p-3">{item.aliquotaIbsUf}</td>
                <td className="p-3">{item.aliquotaIbsMun}</td>
                <td className="p-3">{item.cClasstrib || "-"}</td>
                <td className="p-3">{item.cst || "-"}</td>
                <td className="p-3">{item.exigeGrupo ? "Sim" : "Não"}</td>
                <td className="p-3">{item.naturezaNome || "-"}</td>
                <td className="p-3">{item.emitenteNome || "-"}</td>
                <td className="p-3">{item.gatilho || "-"}</td>
                <td className="p-3">
                  <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item.id)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog Criar/Editar */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar CBS/IBS" : "Novo CBS/IBS"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>UF Origem *</Label>
              <Select value={formData.ufOrigem} onValueChange={(v) => setFormData({ ...formData, ufOrigem: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a UF" />
                </SelectTrigger>
                <SelectContent>
                  {UFS.map((uf) => (
                    <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Município Origem *</Label>
              {selectedMunicipioNome ? (
                <div className="flex gap-2">
                  <Input
                    value={selectedMunicipioNome}
                    disabled
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSelectedMunicipioNome("");
                      setFormData({ ...formData, municipioIbgeId: 0 });
                      setMunicipioSearchTerm("");
                    }}
                  >
                    Limpar
                  </Button>
                </div>
              ) : (
                <>
                  <Input
                    placeholder="Digite para buscar..."
                    value={municipioSearchTerm}
                    onChange={(e) => setMunicipioSearchTerm(e.target.value)}
                  />
                  {municipios.length > 0 && (
                    <div className="border rounded mt-1 max-h-40 overflow-y-auto bg-background">
                      {municipios.map((m: any) => (
                        <div
                          key={m.id}
                          className="p-2 hover:bg-muted cursor-pointer"
                          onClick={() => {
                            setFormData({ ...formData, municipioIbgeId: m.id });
                            setSelectedMunicipioNome(`${m.nome} - ${m.uf}`);
                            setMunicipioSearchTerm("");
                          }}
                        >
                          {m.nome} - {m.uf}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <Label>Alíquota CBS (%) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.aliquotaCbs}
                onChange={(e) => setFormData({ ...formData, aliquotaCbs: e.target.value })}
              />
            </div>

            <div>
              <Label>IBS UF (%) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.aliquotaIbsUf}
                onChange={(e) => setFormData({ ...formData, aliquotaIbsUf: e.target.value })}
              />
            </div>

            <div>
              <Label>IBS Mun (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.aliquotaIbsMun}
                onChange={(e) => setFormData({ ...formData, aliquotaIbsMun: e.target.value })}
              />
            </div>

            <div>
              <Label>C_CLASSTRIB</Label>
              <Input
                value={formData.cClasstrib}
                onChange={(e) => setFormData({ ...formData, cClasstrib: e.target.value })}
              />
            </div>

            <div>
              <Label>CST</Label>
              <Input
                maxLength={3}
                value={formData.cst}
                onChange={(e) => setFormData({ ...formData, cst: e.target.value })}
              />
            </div>

            <div className="flex items-center gap-2 mt-2">
              <Checkbox
                id="exigeGrupo"
                checked={formData.exigeGrupo}
                onCheckedChange={(v) => setFormData({ ...formData, exigeGrupo: !!v })}
              />
              <Label htmlFor="exigeGrupo">CST exige envio do grupo IBS/CBS no XML</Label>
            </div>

            <div>
              <Label>Natureza de Operação</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.naturezaOperacaoNome}
                  readOnly
                  placeholder="Selecione uma natureza de operação"
                />
                <Button
                  type="button"
                  onClick={() => setIsNatureModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Selecionar
                </Button>
                {formData.naturezaOperacaoId !== 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFormData({ ...formData, naturezaOperacaoId: 0, naturezaOperacaoNome: "" })}
                  >
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label>Gatilho</Label>
              <Select
                value={formData.gatilho || ""}
                onValueChange={(v) => setFormData({ ...formData, gatilho: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consumidor_final">Venda para consumidor final</SelectItem>
                  <SelectItem value="sem_st">Venda sem ST</SelectItem>
                  <SelectItem value="com_st">Venda com ST</SelectItem>
                  <SelectItem value="alc">ALC (Área de Livre Comércio)</SelectItem>
                  <SelectItem value="zona_franca">Zona Franca de Manaus</SelectItem>
                  <SelectItem value="amazonia_ocidental">AO (Amazônia Ocidental)</SelectItem>
                  <SelectItem value="nenhum">Nenhum</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!user?.emitenteId && (
              <div>
                <Label>Emitente *</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.emitenteNome}
                    readOnly
                    placeholder="Selecione um emitente"
                  />
                  <Button
                    type="button"
                    onClick={() => setIsEmitenteModalOpen(true)}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    Selecionar
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={resetForm}>Cancelar</Button>
            <Button onClick={handleSubmit}>Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Alterar em Massa */}
      <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
        <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Alterar em Massa ({selectedIds.length} selecionados)</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Alíquota CBS (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={bulkEditData.aliquotaCbs}
                onChange={(e) => setBulkEditData({ ...bulkEditData, aliquotaCbs: e.target.value })}
              />
            </div>

            <div>
              <Label>IBS UF (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={bulkEditData.aliquotaIbsUf}
                onChange={(e) => setBulkEditData({ ...bulkEditData, aliquotaIbsUf: e.target.value })}
              />
            </div>

            <div>
              <Label>IBS Mun (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={bulkEditData.aliquotaIbsMun}
                onChange={(e) => setBulkEditData({ ...bulkEditData, aliquotaIbsMun: e.target.value })}
              />
            </div>

            <div>
              <Label>C_CLASSTRIB</Label>
              <Input
                value={bulkEditData.cClasstrib}
                onChange={(e) => setBulkEditData({ ...bulkEditData, cClasstrib: e.target.value })}
              />
            </div>

            <div>
              <Label>CST</Label>
              <Input
                maxLength={3}
                value={bulkEditData.cst}
                onChange={(e) => setBulkEditData({ ...bulkEditData, cst: e.target.value })}
              />
            </div>

            <div>
              <Label>Exige Grupo XML</Label>
              <select
                className="w-full border rounded px-3 py-2 text-sm mt-1"
                value={bulkEditData.exigeGrupo}
                onChange={(e) => setBulkEditData({ ...bulkEditData, exigeGrupo: e.target.value as "" | "sim" | "nao" })}
              >
                <option value="">Não alterar</option>
                <option value="sim">Sim</option>
                <option value="nao">Não</option>
              </select>
            </div>

            <div>
              <Label>Natureza de Operação</Label>
              <div className="flex gap-2">
                <Input
                  value={bulkEditData.naturezaOperacaoId !== 0
                    ? (naturezas.find((n: any) => n.id === bulkEditData.naturezaOperacaoId) as any)?.nome || ""
                    : ""}
                  readOnly
                  placeholder="Não alterar"
                />
                <Button
                  type="button"
                  onClick={() => setIsBulkNatureModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Selecionar
                </Button>
                {bulkEditData.naturezaOperacaoId !== 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setBulkEditData({ ...bulkEditData, naturezaOperacaoId: 0 })}
                  >
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label>Gatilho</Label>
              <Select
                value={bulkEditData.gatilho || "unchanged"}
                onValueChange={(v) => setBulkEditData({ ...bulkEditData, gatilho: v === "unchanged" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Não alterar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unchanged">Não alterar</SelectItem>
                  <SelectItem value="consumidor_final">Venda para consumidor final</SelectItem>
                  <SelectItem value="sem_st">Venda sem ST</SelectItem>
                  <SelectItem value="com_st">Venda com ST</SelectItem>
                  <SelectItem value="alc">ALC (Área de Livre Comércio)</SelectItem>
                  <SelectItem value="zona_franca">Zona Franca de Manaus</SelectItem>
                  <SelectItem value="amazonia_ocidental">AO (Amazônia Ocidental)</SelectItem>
                  <SelectItem value="nenhum">Nenhum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsBulkDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleBulkEdit}>Aplicar</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Modal de Seleção de Natureza de Operação — Formulário */}
      <Dialog open={isNatureModalOpen} onOpenChange={setIsNatureModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Natureza de Operação</DialogTitle>
          </DialogHeader>
          <Input
            value={natureSearchTerm}
            onChange={(e) => setNatureSearchTerm(e.target.value)}
            placeholder="Buscar por nome, tipo, ID ou emitente..."
            className="mb-3"
          />
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Nome</th>
                  <th className="border-b p-3 text-left">Tipo</th>
                  <th className="border-b p-3 text-left">Emitente</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(naturezas as any[])
                  .filter((n: any) =>
                    natureSearchTerm === "" ||
                    n.nome?.toLowerCase().includes(natureSearchTerm.toLowerCase()) ||
                    n.tipo?.toLowerCase().includes(natureSearchTerm.toLowerCase()) ||
                    String(n.id).includes(natureSearchTerm) ||
                    n.emitenteNome?.toLowerCase().includes(natureSearchTerm.toLowerCase())
                  )
                  .map((n: any) => (
                    <tr key={n.id} className="hover:bg-gray-50">
                      <td className="border-b p-3 text-gray-500 font-mono text-sm">{n.id}</td>
                      <td className="border-b p-3">{n.nome}</td>
                      <td className="border-b p-3">{n.tipo}</td>
                      <td className="border-b p-3 text-sm text-gray-600">{n.emitenteNome || "-"}</td>
                      <td className="border-b p-3 text-center">
                        <Button
                          size="sm"
                          onClick={() => {
                            setFormData({ ...formData, naturezaOperacaoId: n.id, naturezaOperacaoNome: `${n.nome} — ${n.tipo}` });
                            setNatureSearchTerm("");
                            setIsNatureModalOpen(false);
                          }}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Selecionar
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Natureza de Operação — Bulk Edit */}
      <Dialog open={isBulkNatureModalOpen} onOpenChange={setIsBulkNatureModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Natureza de Operação</DialogTitle>
          </DialogHeader>
          <Input
            value={bulkNatureSearchTerm}
            onChange={(e) => setBulkNatureSearchTerm(e.target.value)}
            placeholder="Buscar por nome, tipo, ID ou emitente..."
            className="mb-3"
          />
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Nome</th>
                  <th className="border-b p-3 text-left">Tipo</th>
                  <th className="border-b p-3 text-left">Emitente</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(naturezas as any[])
                  .filter((n: any) =>
                    bulkNatureSearchTerm === "" ||
                    n.nome?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase()) ||
                    n.tipo?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase()) ||
                    String(n.id).includes(bulkNatureSearchTerm) ||
                    n.emitenteNome?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase())
                  )
                  .map((n: any) => (
                    <tr key={n.id} className="hover:bg-gray-50">
                      <td className="border-b p-3 text-gray-500 font-mono text-sm">{n.id}</td>
                      <td className="border-b p-3">{n.nome}</td>
                      <td className="border-b p-3">{n.tipo}</td>
                      <td className="border-b p-3 text-sm text-gray-600">{n.emitenteNome || "-"}</td>
                      <td className="border-b p-3 text-center">
                        <Button
                          size="sm"
                          onClick={() => {
                            setBulkEditData({ ...bulkEditData, naturezaOperacaoId: n.id });
                            setBulkNatureSearchTerm("");
                            setIsBulkNatureModalOpen(false);
                          }}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Selecionar
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Emitente */}
      <Dialog open={isEmitenteModalOpen} onOpenChange={setIsEmitenteModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Emitente</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Razão Social</th>
                  <th className="border-b p-3 text-left">CNPJ</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(emitentes as any[]).map((em: any) => (
                  <tr key={em.id} className="hover:bg-gray-50">
                    <td className="border-b p-3">{em.id}</td>
                    <td className="border-b p-3">{em.razaoSocial}</td>
                    <td className="border-b p-3">{em.cnpj}</td>
                    <td className="border-b p-3 text-center">
                      <Button
                        size="sm"
                        onClick={() => {
                          setFormData({ ...formData, emitenteId: em.id, emitenteNome: em.razaoSocial });
                          setIsEmitenteModalOpen(false);
                        }}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        Selecionar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
